import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import { validate as isUuid } from 'uuid';
import {
  fetchProject,
  resetProject,
  getCurrentProjectData,
  getCurrentUserParticipantType,
} from 'state/Project/projectSlice';
import {
  getAllUsers,
  getLDUsers,
  getFacilitatorUsers,
} from 'state/UsersManagement/usersManagementSlice';
import { ProjectTab } from 'utils/customTypes';
import {
  UPDATE_PROJECT_TABS,
  PROJECT_PARTICIPANT_TYPE,
  SLICE_STATUS,
  PROJECT_STATUS,
  USER_TYPES,
  USER_ROLES,
  PATHS,
} from 'utils/constants';
import {
  getLearningTeams,
  selectLearningTeamStatus,
  selectLearningTeamIsFetched,
} from 'state/LearningTeams/learningTeamsSlice';
import { fetchProjectPermissions } from 'state/Project/permissions/projectPermissionsSlice';
import useGetProjectReadOnly from 'Pages/ProjectPage/hooks/useGetProjectReadOnly';
import PageTitle from 'Molecules/PageTitle/PageTitle';
import TabLink from './components/TabLink/TabLink';
import Overview from './tabs/Overview/Overview';
import Content from './tabs/Content/Content';
import Tasks from './tabs/Tasks/Tasks';
import ProjectTasksList from 'Pages/ProjectTasksList/ProjectTasksList';
import People from './tabs/People/People';
import { resetSidePanelUpdatedData } from 'state/SidePanel/sidePanelSlice';
import {
  selectUserType,
  selectUserId,
  selectUserRole,
} from 'state/User/userSlice';
import BudgetDetailsPage from './tabs/Budget/BudgetDetailsPage';
import Budget from './tabs/Budget/Budget';
import { getProjectLineItemAmounts } from '../../state/BudgetList/budgetListSlice';
import {
  fetchUserTasks,
  selectUserTasks,
} from 'state/ProjectTasks/projectTaskSlice';
const ProjectPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { newTaskListViews = false, budgetManagement = false } = useFlags();
  const { projectId } = useParams<{ projectId: string }>();
  const isValidProjectId = Boolean(projectId) && isUuid(projectId);
  const projectData = useSelector(getCurrentProjectData);
  const fromSidePanel = new URLSearchParams(window.location.search).get(
    'fromSidePanel'
  )
    ? true
    : false;
  const participantType = useSelector(getCurrentUserParticipantType);
  const ldTeamsFetched = useSelector(selectLearningTeamIsFetched);
  const ldTeamStatus = useSelector(selectLearningTeamStatus);
  const isUserCollaborator =
    participantType === PROJECT_PARTICIPANT_TYPE.COLLABORATOR;
  const [currentTab, setCurrentTab] = useState<ProjectTab>(
    UPDATE_PROJECT_TABS.OVERVIEW
  );
  const userType = useSelector(selectUserType);
  const userRole = useSelector(selectUserRole);
  const isFacilitator = userType === USER_TYPES.FACILITATOR;
  const isAdmin = userType === USER_TYPES.L_D && userRole === USER_ROLES.ADMIN;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const defaultTabString = queryParams.get('tab');
  const isProjectClosed = projectData.status === PROJECT_STATUS.CLOSED;
  const currentUserId = useSelector(selectUserId);
  const isCurrentUserProjectOwner = projectData.owners?.includes(currentUserId);
  const isCurrentFacilitatorUserProjectOwner =
    isFacilitator && projectData.owners?.includes(currentUserId);
  const isReadOnly =
    useGetProjectReadOnly(projectData.status, projectData.is_archived) ||
    (isFacilitator && !isCurrentFacilitatorUserProjectOwner);
  const userTasks = useSelector(selectUserTasks);

  const init = useCallback(async () => {
    await Promise.all([
      dispatch(fetchProject(projectId)),
      dispatch(getAllUsers()),
      dispatch(getLDUsers()),
      dispatch(getFacilitatorUsers()),
      dispatch(getProjectLineItemAmounts(projectId)),
      dispatch(fetchUserTasks(projectId)),
      dispatch(fetchProjectPermissions({ projectIds: [projectId] })),
    ]);
  }, [dispatch, projectId]);

  useEffect(() => {
    if (projectId && isValidProjectId) {
      init();
    }
    return () => {
      dispatch(resetProject());
      if (fromSidePanel) {
        dispatch(resetSidePanelUpdatedData);
      }
    };
  }, [fromSidePanel, projectId, isValidProjectId, init, dispatch]);

  useEffect(() => {
    // projectId comes straight off the route; a non-UUID value (e.g. the
    // project's display name instead of its id) would otherwise be sent
    // as-is to every endpoint below and fail server-side.
    if (projectId && !isValidProjectId) {
      history.replace(PATHS.PROJECTS_LIST_PAGE);
    }
  }, [projectId, isValidProjectId, history]);

  useEffect(() => {
    if (projectId && !ldTeamsFetched && ldTeamStatus !== SLICE_STATUS.LOADING) {
      dispatch(getLearningTeams());
    }
  }, [ldTeamStatus, projectId, ldTeamsFetched, dispatch]);

  const notAvailableTabsForCollaborators = useMemo(
    () => [UPDATE_PROJECT_TABS.PEOPLE, UPDATE_PROJECT_TABS.BUDGET],
    []
  );
  const availableTabs = useMemo(() => {
    let tabs: string[] = [];
    const isLD = userType === USER_TYPES.L_D;
    const isBusiness = userType === USER_TYPES.BUSINESS;
    const isExternal = userType === USER_TYPES.EXTERNAL;
    const isProjectTaskAssignee = !isAdmin && userTasks && userTasks.length > 0; // enuf to check this as userTasks is already fetched when current user is assigned to it
    const isProjectParticipant =
      participantType === PROJECT_PARTICIPANT_TYPE.COLLABORATOR ||
      participantType === PROJECT_PARTICIPANT_TYPE.MEMBER;
    const shouldShowBudgetTab = (isLD && isAdmin) || isCurrentUserProjectOwner;
    for (let tab of Object.values(UPDATE_PROJECT_TABS)) {
      if (
        isUserCollaborator &&
        notAvailableTabsForCollaborators.includes(tab) &&
        !isFacilitator
      ) {
        continue;
      }

      if (tab === 'budget') {
        // Hide if should NOT show, or for user types that should never see budget
        if (
          !shouldShowBudgetTab ||
          isBusiness ||
          isExternal ||
          isProjectTaskAssignee ||
          isProjectParticipant ||
          (!isLD && isFacilitator)
        ) {
          continue;
        }
      }

      tabs.push(tab);
    }
    return tabs;
  }, [
    notAvailableTabsForCollaborators,
    isUserCollaborator,
    isFacilitator,
    userType,
    isAdmin,
    userTasks,
    isCurrentUserProjectOwner,
    participantType,
  ]);

  useEffect(() => {
    if (defaultTabString && availableTabs.includes(defaultTabString)) {
      setCurrentTab(defaultTabString);
    } else {
      setCurrentTab(UPDATE_PROJECT_TABS.OVERVIEW);
    }
  }, [defaultTabString, availableTabs]);

  let projectTitle = get(projectData, 'title');
  if (projectTitle && projectTitle.length > 80) {
    projectTitle = `${projectTitle.substring(0, 80)}...`;
  }

  if (projectId && !isValidProjectId) {
    return null;
  }

  return (
    <div className='h-full flex flex-col'>
      <PageTitle
        titleComponent={projectTitle}
        headerChildren={
          <div className='flex'>
            {availableTabs.map((tab) => {
              return (
                <TabLink
                  key={tab}
                  tabKey={tab}
                  isActive={currentTab === tab}
                  setCurrentTab={setCurrentTab}
                  fromSidePanel={fromSidePanel}
                />
              );
            })}
          </div>
        }
      />
      {currentTab === UPDATE_PROJECT_TABS.OVERVIEW && (
        <Overview
          projectId={projectId}
          fromSidePanel={fromSidePanel}
          isReadOnly={isReadOnly}
        />
      )}
      {currentTab === UPDATE_PROJECT_TABS.TASKS &&
        (newTaskListViews ? (
          <ProjectTasksList
            projectId={projectId}
            projectStatus={projectData.status || PROJECT_STATUS.NEW}
            isProjectArchived={projectData.is_archived}
            isUserFacilitator={isFacilitator}
          />
        ) : (
          <Tasks />
        ))}
      {currentTab === UPDATE_PROJECT_TABS.BUDGET &&
        (budgetManagement ? (
          <BudgetDetailsPage />
        ) : (
          <Budget
            fromSidePanel={fromSidePanel}
            templateId={projectData.project_template_id}
            isReadOnly={isReadOnly}
          />
        ))}
      {currentTab === UPDATE_PROJECT_TABS.PEOPLE && (
        <People
          projectId={projectId}
          fromSidePanel={fromSidePanel}
          isReadOnly={
            isReadOnly || projectData.status === PROJECT_STATUS.CANCELED
          }
        />
      )}
      {currentTab === UPDATE_PROJECT_TABS.CONTENT && (
        <Content
          projectId={projectId}
          isReadOnly={
            (isFacilitator && !isCurrentFacilitatorUserProjectOwner) ||
            isProjectClosed ||
            projectData.is_archived ||
            false
          }
        />
      )}
    </div>
  );
};

export default ProjectPage;
